/**
 * ImpersonationBanner — shown to governors who are logged in AS another
 * tenant's user. Prominent amber banner with an "End impersonation" button
 * that restores the governor's previous CRM session.
 */
import { useEffect, useState } from 'react';
import { AlertTriangle, X } from 'lucide-react';
import { toast } from 'sonner';

export default function ImpersonationBanner() {
  const [info, setInfo] = useState(null);

  useEffect(() => {
    const raw = localStorage.getItem('_impersonation_active');
    if (!raw) return;
    try {
      const parsed = JSON.parse(raw);
      if (new Date(parsed.expires_at).getTime() < Date.now()) {
        // Expired — auto-clean
        endImpersonation(true);
        return;
      }
      setInfo(parsed);
    } catch {
      // malformed
    }
  }, []);

  const endImpersonation = (silent = false) => {
    const prev = localStorage.getItem('_governor_prev_token');
    if (prev) {
      localStorage.setItem('access_token', prev);
      localStorage.removeItem('_governor_prev_token');
    } else {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
    }
    localStorage.removeItem('_impersonation_active');
    if (!silent) toast.success('Impersonation ended — restored governor session');
    // Force full reload so all auth-dependent hooks re-initialise
    window.location.href = '/superadmin/login';
  };

  if (!info) return null;

  return (
    <div
      className="bg-amber-500 text-amber-950 px-4 py-2 flex items-center gap-3 text-sm font-medium shadow-md border-b border-amber-600"
      data-testid="impersonation-banner"
    >
      <AlertTriangle className="w-4 h-4 shrink-0" />
      <span className="truncate">
        Impersonating <strong>{info.user_email}</strong> ({info.tenant_name}) · expires{' '}
        {new Date(info.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
      </span>
      <div className="flex-1" />
      <button
        onClick={() => endImpersonation(false)}
        className="inline-flex items-center gap-1 px-3 py-1 rounded-md bg-amber-950/10 hover:bg-amber-950/20 transition text-xs font-semibold"
        data-testid="impersonation-end"
      >
        <X className="w-3 h-3" /> End impersonation
      </button>
    </div>
  );
}
